/**
 * @fileoverview Section "Loading states" — Skeleton, Progress, Spinner.
 *
 * Montre les trois patrons de chargement GNPC :
 *   - `Skeleton` quand la forme du contenu est connue ;
 *   - `Progress` quand l'avancement est borné ;
 *   - `Spinner` quand l'attente est courte ou indéterminée.
 */

import {
  Button,
  Progress,
  Skeleton,
  SkeletonAvatar,
  SkeletonCard,
  SkeletonRow,
  Spinner,
} from '@gnpc/ui';
import { DsPreview } from '../components/DsPreview.tsx';

export function DsSectionLoading(): JSX.Element {
  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-quipux-navy-950">Skeleton</h3>
        <div className="grid gap-4 lg:grid-cols-3">
          <DsPreview label="Primitive — forme libre">
            <div className="w-full space-y-3">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-24 w-full" />
            </div>
          </DsPreview>
          <DsPreview label="SkeletonRow — listes et tables">
            <div className="w-full space-y-4">
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
          </DsPreview>
          <DsPreview label="SkeletonCard — panneau de détail">
            <div className="w-full">
              <SkeletonCard />
            </div>
          </DsPreview>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-quipux-navy-950">Spinner</h3>
        <div className="grid gap-4 lg:grid-cols-3">
          <DsPreview label="Icône seule — bouton">
            <Button disabled>
              <Spinner />
              Traitement
            </Button>
          </DsPreview>
          <DsPreview label="Avec label — role=status">
            <Spinner label="Vérification de votre paiement..." />
          </DsPreview>
          <DsPreview label="Tailles">
            <div className="flex items-center gap-4">
              <Spinner size="sm" />
              <Spinner size="md" />
              <Spinner size="lg" />
            </div>
          </DsPreview>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-quipux-navy-950">Progress</h3>
        <div className="grid gap-4 lg:grid-cols-2">
          <DsPreview label="Export backoffice — progression bornée">
            <Progress label="Export audit en préparation" value={62} showValue />
          </DsPreview>
          <DsPreview label="Import/rejeu — valeurs bornées">
            <div className="w-full space-y-4">
              <Progress ariaLabel="Progression faible" value={18} />
              <Progress ariaLabel="Progression forte" value={86} />
            </div>
          </DsPreview>
        </div>
      </div>

      <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4">
        <SkeletonAvatar />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-quipux-navy-950">Règle d'usage</p>
          <p className="text-xs text-muted-foreground">
            Skeleton pour chargement initial, Spinner pour attente courte, Progress pour
            opération longue avec pourcentage connu.
          </p>
        </div>
      </div>
    </div>
  );
}
